import { MessageType } from './system-message-templates';

export type VariableGroup = 'client' | 'practitioner' | 'appointment' | 'payment';

export interface MessageVariable {
  name: string;
  description: string;
  group: VariableGroup;
}

export const SYSTEM_MESSAGE_VARIABLES: Record<MessageType, MessageVariable[]> = {
  appointment_confirmation: [
    // Client variables
    { name: 'first_name', description: "Client's first name", group: 'client' },
    { name: 'name', description: "Client's full name", group: 'client' },
    { name: 'phone', description: "Client's phone number", group: 'client' },
    { name: 'email', description: "Client's email address", group: 'client' },
    { name: 'age', description: "Client's age", group: 'client' },
    { name: 'gender', description: "Client's gender", group: 'client' },
    { name: 'goal', description: "Client's health goal", group: 'client' },

    // Appointment variables
    { name: 'title', description: 'Appointment title', group: 'appointment' },
    { name: 'appointment_date', description: 'Appointment date (formatted)', group: 'appointment' },
    { name: 'appointment_time', description: 'Appointment time', group: 'appointment' },
    { name: 'appointment_day_name', description: 'Day of the week', group: 'appointment' },
    { name: 'duration', description: 'Appointment duration', group: 'appointment' },
    { name: 'meeting_type', description: 'Meeting type (Video Call, In Person, etc.)', group: 'appointment' },
    { name: 'meeting_details', description: 'Meeting details (location or link)', group: 'appointment' },
    { name: 'meeting_link', description: 'Meeting link (if video call)', group: 'appointment' },
    { name: 'time_until_appointment', description: 'Time until appointment (e.g., "in 2 days")', group: 'appointment' },

    // Practitioner variables
    { name: 'dietitian_name', description: 'Your name', group: 'practitioner' },
    { name: 'dietitian_phone', description: 'Your phone number', group: 'practitioner' },
    { name: 'dietitian_organization', description: 'Your organization name', group: 'practitioner' },
  ],

  appointment_reminder: [
    // Client variables
    { name: 'first_name', description: "Client's first name", group: 'client' },
    { name: 'name', description: "Client's full name", group: 'client' },
    { name: 'phone', description: "Client's phone number", group: 'client' },
    { name: 'email', description: "Client's email address", group: 'client' },
    { name: 'age', description: "Client's age", group: 'client' },
    { name: 'gender', description: "Client's gender", group: 'client' },
    { name: 'goal', description: "Client's health goal", group: 'client' },

    // Appointment variables
    { name: 'title', description: 'Appointment title', group: 'appointment' },
    { name: 'appointment_date', description: 'Appointment date (formatted)', group: 'appointment' },
    { name: 'appointment_time', description: 'Appointment time', group: 'appointment' },
    { name: 'appointment_day_name', description: 'Day of the week', group: 'appointment' },
    { name: 'duration', description: 'Appointment duration', group: 'appointment' },
    { name: 'meeting_type', description: 'Meeting type (Video Call, In Person, etc.)', group: 'appointment' },
    { name: 'meeting_details', description: 'Meeting details (location or link)', group: 'appointment' },
    { name: 'meeting_link', description: 'Meeting link (if video call)', group: 'appointment' },
    { name: 'time_until_appointment', description: 'Time until appointment (e.g., "in 2 hours")', group: 'appointment' },

    // Practitioner variables
    { name: 'dietitian_name', description: 'Your name', group: 'practitioner' },
    { name: 'dietitian_phone', description: 'Your phone number', group: 'practitioner' },
    { name: 'dietitian_organization', description: 'Your organization name', group: 'practitioner' },
  ],

  payment_reminder: [
    // Client variables
    { name: 'first_name', description: "Client's first name", group: 'client' },
    { name: 'name', description: "Client's full name", group: 'client' },
    { name: 'phone', description: "Client's phone number", group: 'client' },

    // Payment variables
    { name: 'amount', description: 'Payment amount (formatted with currency)', group: 'payment' },
    { name: 'due_date', description: 'Payment due date', group: 'payment' },
    { name: 'payment_method', description: 'Payment method (UPI, Cash, etc.)', group: 'payment' },
    { name: 'payment_status', description: 'Payment status', group: 'payment' },

    // Practitioner variables
    { name: 'practitioner_name', description: 'Your name', group: 'practitioner' },
    { name: 'practitioner_phone', description: 'Your phone number', group: 'practitioner' },
    { name: 'practitioner_organization', description: 'Your organization name', group: 'practitioner' },
  ],

  payment_received: [
    // Client variables
    { name: 'first_name', description: "Client's first name", group: 'client' },
    { name: 'name', description: "Client's full name", group: 'client' },
    { name: 'phone', description: "Client's phone number", group: 'client' },

    // Payment variables
    { name: 'amount', description: 'Payment amount (formatted with currency)', group: 'payment' },

    // Practitioner variables
    { name: 'practitioner_name', description: 'Your name', group: 'practitioner' },
    { name: 'practitioner_phone', description: 'Your phone number', group: 'practitioner' },
    { name: 'practitioner_organization', description: 'Your organization name', group: 'practitioner' },
  ],
};

// Helper to get variables grouped by category
export function getVariablesByGroup(messageType: MessageType) {
  const variables = SYSTEM_MESSAGE_VARIABLES[messageType];
  const grouped: Record<VariableGroup, MessageVariable[]> = {
    client: [],
    practitioner: [],
    appointment: [],
    payment: [],
  };

  variables.forEach((variable) => {
    grouped[variable.group].push(variable);
  });

  return grouped;
}

// Helper to get all variable names for a message type
export function getVariableNames(messageType: MessageType): string[] {
  return SYSTEM_MESSAGE_VARIABLES[messageType].map((v) => v.name);
}
